import Raven from 'raven-js';
import enviro from 'enviro';
import { Component, useMemo } from 'react';
import { makeAsyncCardComponent } from '../internal/makeAsyncCardComponent';
import ActionErrorTag from '../action-formatters/ActionErrorTag';
import { Metrics } from '../internal/Metrics';
import { useCardHookContext } from '../internal/useCardHookContext';
import { useCardLocationContext } from 'crm-cards-sdk/internal/CardLocationContext';
import { jsx as _jsx } from "react/jsx-runtime";
function actionConfigIsComponent(actionConfigs) {
  if (typeof actionConfigs === 'function') {
    return true;
  }
  return false;
}
class LegacyActionErrorBoundary extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      error: undefined
    };
  }
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
  componentDidCatch(error) {
    console.error(error);
    Metrics.counter('card-action-errors', Object.assign({
      card_type: this.props.cardType,
      is_legacy_action: 'true',
      location: this.props.location
    }, this.props.loggingTag ? {
      logging_tag: this.props.loggingTag
    } : {})).increment();
    Raven.captureException(error, {
      extra: {
        component: 'CrmCardActionResolver (Legacy Action)',
        cardType: this.props.cardType
      }
    });
  }
  render() {
    if (this.state.error) {
      return /*#__PURE__*/_jsx(ActionErrorTag, {});
    }
    return this.props.children;
  }
}
const DefaultUseCardActions = () => {
  return undefined;
};
export function withCardActionsResolver({
  enableLegacyActionFormat,
  useCardActions = DefaultUseCardActions,
  cardTypesEnabledForLegacyActions
}) {
  return CardRenderer => {
    function CardActionResolver(props) {
      const {
        cardProps,
        cardData
      } = props;
      const cardHookContext = useCardHookContext();
      const cardLocationContext = useCardLocationContext();

      // This can be a component or and object that defines the actions
      const cardActions = useCardActions(props, cardHookContext);

      // This is when we are using an object to define what the actions should
      // look like for the card
      const actionConfigs = useMemo(() => {
        if (cardActions && actionConfigIsComponent(cardActions)) {
          return undefined;
        }
        return cardActions;
      }, [cardActions]);

      // This is when we are using an component as the definition of the actions.
      // This can only be done when `enableLegacyActionFormat` is true.
      // This MUST be done separately from rendering with the props. Otherwise a new async
      // component will be created and there will be extra unmounting and mounting, potentially
      // leading to unexpected/undesireable behaviors
      const AsyncCardActionsComponent = useMemo(() => {
        if (cardActions && enableLegacyActionFormat && actionConfigIsComponent(cardActions)) {
          // If the card isn't enabled for legacy actions then throw an error
          if (!enviro.isProd() && !cardTypesEnabledForLegacyActions.includes(cardData.definition.cardType)) {
            throw new Error(`Card type ${cardData.definition.cardType} is not enabled for legacy actions in the crm-sdk. Please check with #crm-card-infra-frontend if you need to enable this card type.`);
          }
          return makeAsyncCardComponent({
            loader: cardActions,
            disableLoadingComponent: true
          });
        }
        return undefined;
      }, [cardActions, cardData.definition.cardType]);
      const actionComponent = useMemo(() => {
        if (AsyncCardActionsComponent) {
          return /*#__PURE__*/_jsx(LegacyActionErrorBoundary, {
            cardType: cardData.definition.cardType,
            location: cardLocationContext.location,
            loggingTag: cardLocationContext.loggingTag,
            children: /*#__PURE__*/_jsx(AsyncCardActionsComponent, Object.assign({}, cardProps))
          });
        }
        return undefined;
      }, [cardProps, cardData.definition.cardType, AsyncCardActionsComponent, cardLocationContext.location, cardLocationContext.loggingTag]);
      return /*#__PURE__*/_jsx(CardRenderer, Object.assign({}, props, {
        actionConfigs: actionConfigs,
        actionComponent: actionComponent
      }));
    }
    class CrmCardActionErrorBoundary extends Component {
      constructor(...args) {
        super(...args);
        this.state = {
          error: undefined
        };
      }
      static getDerivedStateFromError(error) {
        return {
          error
        };
      }
      componentDidCatch(error) {
        console.error(error);
        Metrics.counter('card-action-errors', Object.assign({
          card_type: this.props.cardData.definition.cardType,
          location: this.props.location
        }, this.props.loggingTag ? {
          logging_tag: this.props.loggingTag
        } : {})).increment();
        Raven.captureException(error, {
          extra: {
            location: this.props.location,
            loggingTag: this.props.loggingTag,
            component: 'CrmCardActionResolver',
            cardType: this.props.cardData
          }
        });
      }
      render() {
        if (this.state.error) {
          return /*#__PURE__*/_jsx(CardRenderer, Object.assign({}, this.props, {
            hasActionsLoadingError: true
          }));
        }
        return this.props.children;
      }
    }
    return function CardActionResolverWithErrorBoundary(props) {
      const cardLocationContext = useCardLocationContext();
      return /*#__PURE__*/_jsx(CrmCardActionErrorBoundary, Object.assign({}, props, {
        location: cardLocationContext.location,
        loggingTag: cardLocationContext.loggingTag,
        children: /*#__PURE__*/_jsx(CardActionResolver, Object.assign({}, props))
      }));
    };
  };
}